import { useAuthStore } from '~/stores/auth'
import { getAccessToken } from '~/utils/authToken'
import { isPublicPath } from '~/utils/publicPaths'

export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) {
    return
  }

  const auth = useAuthStore()
  const isPublicRoute = isPublicPath(to.path)

  if (!auth.isLoaded) {
    // Keep auth state warm without blocking the first paint.
    void auth.loadMe().catch((error) => {
      console.warn('[auth] Global warm-up failed:', error)
    })
  }

  if (isPublicRoute) {
    if (to.path === '/login') {
      // Avoid redirecting away from /login on stale in-memory auth state —
      // only redirect when auth has been validated this session.
      if (!auth.isLoaded && getAccessToken()) {
        try {
          await auth.loadMe()
        } catch (error) {
          console.warn('[auth] Login-route loadMe failed:', error)
        }
      }
      if (auth.isLoaded && auth.isAuthenticated) {
        return navigateTo('/')
      }
    }
    return
  }

  // Permission gate: routes can declare requiresAll / requiresAny in
  // definePageMeta. Authentication itself is enforced separately by the
  // named `auth` middleware on each protected page.
  const requiresAll = (to.meta.requiresAll ?? []) as string[]
  const requiresAny = (to.meta.requiresAny ?? []) as string[]

  if (requiresAll.length === 0 && requiresAny.length === 0) {
    return
  }

  // Need a loaded auth state to evaluate permissions. If we have a token but
  // haven't validated it yet, wait for /me to resolve.
  if (!auth.isLoaded && getAccessToken()) {
    try {
      await auth.loadMe()
    } catch (error) {
      console.warn('[auth] Permission-check loadMe failed:', error)
    }
  }

  // Not signed in — let the named auth middleware redirect to /login.
  if (!auth.isAuthenticated) {
    return
  }

  const passesAll = requiresAll.length === 0 || auth.hasAll(requiresAll)
  const passesAny = requiresAny.length === 0 || auth.hasAny(requiresAny)

  if (!passesAll || !passesAny) {
    return navigateTo('/not-authorized')
  }
})
